/**
 * A git workspace's `apiboo.yaml` carries its project (project scope card
 * 2.2), so a clone on another device — or a teammate's — lands in the same
 * project instead of in "No project":
 *
 *   id: ws-…
 *   name: Shop API
 *   project:
 *     color: "#22c55e"
 *     id: p-shop
 *     name: Shop
 *   storage: git
 *
 * Write: the project this device binds the workspace to (projectBlockFor).
 * A device with no binding keeps the repository's block as it is
 * (keepProjectBlock, called from repoIdentity.keepRepoIdentity) — otherwise
 * two devices would rewrite each other's file on every sync, the trap
 * repoIdentity.js was written for.
 *
 * Read: cloning a repository whose manifest names a project binds the new
 * workspace to that project when this user has it (and may bind it), else
 * creates a personal project with the same name and colour (adoptManifestProject).
 * Nothing secret is in the block: an id, a name, a colour.
 */
import { saveProject } from '../graph/projectsStore';
import { teamIdOfScope } from '../graph/graphScopes';
import { personalScopeId } from '../services/personalScope';
import { bindWorkspace } from './projectBinding';
import { bindingOf, bindingRefusal } from './projectBindingFields';
import { useProjectScopeStore } from './projectScopeStore';
import { refreshProjectScope } from './projectScopeLoader';
import { enqueueScopeJob } from './projectScopeJobs';
import { projectBlockFor } from './projectManifestBlock';

export { keepProjectBlock, projectFromManifest } from './projectManifestText';
export { projectBlockFor } from './projectManifestBlock';

/**
 * After a clone: put the new workspace in the manifest's project. Never
 * throws; returns the project it bound to, or null.
 */
export async function adoptManifestProject(workspace, block) {
  if (!workspace?.id || !block) return null;
  try {
    if (!useProjectScopeStore.getState().known.loaded) await enqueueScopeJob(() => refreshProjectScope().catch(() => {}));
    const { projects } = useProjectScopeStore.getState().known;
    if (projectBlockFor(workspace.id, projects)) return null; // already in one on this device
    const same = (projects || []).find((p) => p && String(p.id) === block.id);
    let saved;
    if (same && !bindingRefusal(teamIdOfScope(same.scopeKey), workspace)) {
      saved = await bindWorkspace(same, workspace);
    } else {
      // Not this user's project (or a team one the clone cannot join): a
      // personal one with the same name and colour. A fresh id when the id
      // is taken, so two records never share one.
      const draft = { name: block.name, ...(block.color ? { color: block.color } : {}), ...bindingOf(workspace) };
      saved = await saveProject(same ? draft : { ...draft, id: block.id }, personalScopeId());
    }
    await enqueueScopeJob(() => refreshProjectScope().catch(() => {}));
    return saved || null;
  } catch (err) {
    console.warn('[project-scope] could not put the cloned workspace in its project:', err);
    return null;
  }
}
